import React from "react";
import { Tabs } from "@/components/ui/tabs";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DashboardTab from "@/components/admin/DashboardTab";
import BookingsTable from "@/components/admin/BookingsTable";
import DocumentsTab from "@/components/admin/DocumentsTab";
import PaymentsTab from "@/components/admin/PaymentsTab";
import RemindersTab from "@/components/admin/RemindersTab";
import UstadzTab from "@/components/admin/UstadzTab";
import PostersTab from "@/components/admin/PostersTab";
import MessagesTable from "@/components/admin/MessagesTable";
import AlumniReviewTab from "@/components/admin/AlumniReviewTab";

// Simple Tabs wrapper that mounts just one <TabsContent>
function TabWrap({ value, children }) {
  return <Tabs value={value} defaultValue={value} className="w-full">{children}</Tabs>;
}

// ==================================================
// 1. Dashboard route
// ==================================================
export function AdminDashboardPage() {
  return <TabWrap value="dashboard"><DashboardTab /></TabWrap>;
}

// ==================================================
// 2. Bookings route
// ==================================================
export function AdminBookingsPage() {
  const { authHeaders } = useAuth();
  const [bookings, setBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bookings", { headers: authHeaders() });
      setBookings(r.data);
    } catch { toast.error("Gagal memuat bookings"); }
    finally { setLoading(false); }
  }, [authHeaders]);

  React.useEffect(() => { load(); }, [load]);

  const updateBookingStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status }, { headers: authHeaders() });
      toast.success("Status booking diperbarui");
      load();
    } catch { toast.error("Gagal update status"); }
  };
  const removeBooking = async (id) => {
    if (!window.confirm("Hapus booking ini? Aksi tidak dapat dibatalkan.")) return;
    try {
      await api.delete(`/admin/bookings/${id}`, { headers: authHeaders() });
      toast.success("Booking dihapus");
      load();
    } catch { toast.error("Gagal menghapus"); }
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground text-sm">Memuat bookings…</div>;
  return (
    <div className="space-y-4" data-testid="admin-bookings-page">
      <BookingsTable bookings={bookings} onDelete={removeBooking} onStatusChange={updateBookingStatus} />
    </div>
  );
}

// ==================================================
// 3. Posters route
// ==================================================
export function AdminPostersPage() {
  return <TabWrap value="posters"><PostersTab /></TabWrap>;
}

// ==================================================
// 4. Dokumen route
// ==================================================
export function AdminDocumentsPage() {
  return <TabWrap value="documents"><DocumentsTab /></TabWrap>;
}

// ==================================================
// 5. Pembayaran route
// ==================================================
export function AdminPaymentsPage() {
  return <TabWrap value="payments"><PaymentsTab /></TabWrap>;
}

// ==================================================
// 6. Konten route — placeholder (CRUD in next batch)
// ==================================================
export function AdminContentPage() {
  return (
    <div className="bg-card border border-border rounded-lg p-8 text-center" data-testid="admin-content-placeholder">
      <h2 className="font-serif text-primary text-2xl">Konten &amp; Blog — Batch 4</h2>
      <p className="text-muted-foreground mt-2 text-sm max-w-lg mx-auto">
        CMS Blog + kelola Testimoni + Galeri + Ustadz + Manasik + Broadcast Pengumuman akan tersedia di Batch 4. Sementara ini, kelola Ustadz &amp; Reminder via menu di bawah.
      </p>
      <div className="mt-6 flex justify-center gap-2 flex-wrap">
        <a href="/admin/ustadz" className="btn-outline text-xs">Kelola Ustadz</a>
        <a href="/admin/reminders" className="btn-outline text-xs">Reminder Otomatis</a>
      </div>
    </div>
  );
}

// Additional stand-alone pages for existing tabs
export function AdminUstadzPage() {
  return <TabWrap value="ustadz"><UstadzTab /></TabWrap>;
}
export function AdminRemindersPage() {
  return <TabWrap value="reminders"><RemindersTab /></TabWrap>;
}
export function AdminAlumniPage() {
  return <AlumniReviewTab />;
}
export function AdminMessagesPage() {
  const { authHeaders } = useAuth();
  const [msgs, setMsgs] = React.useState([]);
  React.useEffect(() => {
    api.get("/admin/contact", { headers: authHeaders() })
      .then((r) => setMsgs(r.data))
      .catch(() => toast.error("Gagal memuat pesan kontak"));
  }, [authHeaders]);
  return (
    <div data-testid="admin-messages-page">
      <MessagesTable messages={msgs} onDelete={async (id) => {
        try { await api.delete(`/admin/contact/${id}`, { headers: authHeaders() }); setMsgs((p) => p.filter((m) => m.id !== id)); toast.success("Pesan dihapus"); }
        catch { toast.error("Gagal menghapus"); }
      }} />
    </div>
  );
}

// ==================================================
// 7. Kelola Paket route — Excel import + TipTap-ready poster manager
// ==================================================
import ExcelImportCard from "@/components/admin/ExcelImportCard";

export function AdminPackagesPage() {
  const [importKey, setImportKey] = React.useState(0);
  return (
    <div className="space-y-6" data-testid="admin-packages-page">
      <ExcelImportCard onImported={() => setImportKey((k) => k + 1)} />
      <TabWrap key={importKey} value="posters"><PostersTab /></TabWrap>
    </div>
  );
}

// ==================================================
// 8. Kelola Jamaah — Batch 3 (full table + detail routing)
// ==================================================
import { useEffect as _useEffect, useState as _useState } from "react";
import { Input as _Input } from "@/components/ui/input";
import { Button as _Button } from "@/components/ui/button";
import { toast as _toast } from "sonner";
import { Search, Trash2, RotateCcw, Eye, User as UserIcon2, Loader2 as Loader2Ico } from "lucide-react";
import { Link as _Link } from "react-router-dom";
import axios from "axios";

const _jamaahApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const _fmtDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "-"; }
};

export function AdminJamaahPage() {
  const [items, setItems] = _useState([]);
  const [total, setTotal] = _useState(0);
  const [q, setQ] = _useState("");
  const [status, setStatus] = _useState("all");
  const [loading, setLoading] = _useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (status && status !== "all") params.set("status", status);
      params.set("limit", "200");
      const { data } = await _jamaahApi.get(`/admin/jamaah?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { _toast.error("Gagal memuat data jamaah"); }
    finally { setLoading(false); }
  };

  _useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const softDelete = async (uid) => {
    if (!window.confirm("Nonaktifkan jamaah ini?")) return;
    try { await _jamaahApi.patch(`/admin/jamaah/${uid}/soft-delete`); _toast.success("Jamaah dinonaktifkan"); load(); }
    catch { _toast.error("Gagal menonaktifkan"); }
  };
  const restore = async (uid) => {
    try { await _jamaahApi.patch(`/admin/jamaah/${uid}/restore`); _toast.success("Direstore"); load(); }
    catch { _toast.error("Gagal restore"); }
  };

  return (
    <div data-testid="admin-jamaah-page">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl text-primary">Kelola Jamaah</h2>
          <p className="text-sm text-muted-foreground">Total {total} jamaah terdaftar.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <_Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, email, telepon…" className="pl-9 w-72" data-testid="jamaah-search" />
            </div>
            <_Button type="submit" data-testid="jamaah-search-btn">Cari</_Button>
          </form>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-border rounded-md px-3 py-2 text-sm bg-background" data-testid="jamaah-status-filter">
            <option value="all">Semua status</option>
            <option value="active">Aktif</option>
            <option value="inactive">Nonaktif</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto border border-border rounded-lg bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-primary">
            <tr>
              <th className="text-left p-3 font-medium">Nama</th>
              <th className="text-left p-3 font-medium">Email</th>
              <th className="text-left p-3 font-medium">Telepon</th>
              <th className="text-right p-3 font-medium">Booking</th>
              <th className="text-right p-3 font-medium">Pax</th>
              <th className="text-left p-3 font-medium">Booking Terakhir</th>
              <th className="text-left p-3 font-medium">Terdaftar</th>
              <th className="text-right p-3 font-medium">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">
                <Loader2Ico className="inline h-4 w-4 animate-spin mr-2" /> Memuat…
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Tidak ada jamaah ditemukan.</td></tr>
            )}
            {!loading && items.map((u) => (
              <tr key={u.user_id} data-testid={`jamaah-row-${u.user_id}`}
                  className={`border-t border-border hover:bg-accent/5 ${u.soft_deleted ? "opacity-50" : ""}`}>
                <td className="p-3 font-medium text-primary">
                  <div className="flex items-center gap-2">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-accent/15 grid place-items-center text-accent"><UserIcon2 className="h-3.5 w-3.5" /></div>
                    )}
                    <span>{u.name || "-"}</span>
                    {u.is_admin && <span className="text-[10px] uppercase tracking-wide bg-primary text-white rounded-full px-2 py-0.5">Admin</span>}
                    {u.soft_deleted && <span className="text-[10px] uppercase tracking-wide bg-red-500 text-white rounded-full px-2 py-0.5">Nonaktif</span>}
                  </div>
                </td>
                <td className="p-3 text-muted-foreground">{u.email}</td>
                <td className="p-3 text-muted-foreground">{u.phone || "-"}</td>
                <td className="p-3 text-right">{u.bookings_count || 0}</td>
                <td className="p-3 text-right">{u.total_pax || 0}</td>
                <td className="p-3 text-muted-foreground">{_fmtDate(u.last_booking_at)}</td>
                <td className="p-3 text-muted-foreground">{_fmtDate(u.created_at)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <_Link to={`/admin/jamaah/${u.user_id}`} data-testid={`jamaah-detail-${u.user_id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:text-accent mr-3">
                    <Eye className="h-3.5 w-3.5" /> Detail
                  </_Link>
                  {u.soft_deleted ? (
                    <button onClick={() => restore(u.user_id)} className="inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-900" data-testid={`jamaah-restore-${u.user_id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Restore
                    </button>
                  ) : (
                    !u.is_admin && (
                      <button onClick={() => softDelete(u.user_id)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800" data-testid={`jamaah-delete-${u.user_id}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Nonaktif
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================================================
// 9. Pengaturan — Superadmin: staff management
// ==================================================
import StaffManagement from "@/components/admin/StaffManagement";
import BroadcastWA from "@/components/admin/BroadcastWA";

export function AdminSettingsPage() {
  return <StaffManagement />;
}

export function AdminBroadcastPage() {
  return <BroadcastWA />;
}

import ManasikAdmin from "@/components/admin/ManasikAdmin";
export function AdminManasikPage() {
  return <ManasikAdmin />;
}
